import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CgSpinner } from 'react-icons/cg';
import { FaPerson } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { FaArrowLeft } from 'react-icons/fa';

import { userAppData } from '../MainApp.js';
import Header from '../common/header.js';
import {
  getRotaStaffPerms,
  getRotasBootstrap,
  updateRotaStaffAllowOverride,
  updateRotaStaffPerms,
} from './sysManagerRotasApi.js';

import './SysManagerRotasDesktop.css';
import './SysManagerRotasMobile.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };
const ACCESS_OPTIONS = [
  { value: 'NONE', label: 'None' },
  { value: 'VIEW_SELF_ONLY', label: 'View self only' },
  { value: 'VIEW_ALL', label: 'View all' },
  { value: 'EDIT', label: 'Edit' },
];
const ACCESS_FILTER_OPTIONS = [
  {
    value: 'ALL',
    label: 'All',
    description: 'Includes every staff member, whatever rota access they currently have.',
  },
  {
    value: 'VIEW_ALL',
    label: 'View All',
    description: 'Can view the full rota and all assignments, but cannot edit it.',
  },
  {
    value: 'VIEW_SELF_ONLY',
    label: 'View Only',
    description: 'Can only view blocks they are personally assigned to.',
  },
  {
    value: 'NONE',
    label: 'None',
    description: 'Has no extra rota access for this rota family.',
  },
  {
    value: 'EDIT',
    label: 'Edit',
    description: 'Can view the full rota and make changes to this rota family.',
  },
];
const ACCESS_LABEL_LOOKUP = ACCESS_OPTIONS.reduce((lookup, option) => {
  lookup[option.value] = option.label;
  return lookup;
}, {});

function getAvatarInitial(name) {
  const cleanName = String(name || '').trim();
  if (!cleanName) {
    return '?';
  }
  return cleanName.charAt(0).toUpperCase();
}

function normaliseImageUrl(urlValue) {
  const cleanValue = String(urlValue || '').trim();
  if (!cleanValue) {
    return '';
  }
  const lowerValue = cleanValue.toLowerCase();
  if (lowerValue === 'none' || lowerValue === 'null' || lowerValue === 'undefined') {
    return '';
  }
  return cleanValue;
}

function createManageAccessState() {
  return {
    open: false,
    rota: null,
    loading: false,
    items: [],
    searchText: '',
    accessFilter: 'ALL',
    allowOverrideOnly: false,
  };
}

function createUpdatingState() {
  return {
    accessLevel: false,
    allowOverride: false,
  };
}

export default function SysManagerRotasHome() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rotas, setRotas] = useState([]);
  const [manageAccessState, setManageAccessState] = useState(createManageAccessState);
  const [updatingCogIds, setUpdatingCogIds] = useState({});

  const filteredManageAccessItems = useMemo(() => {
    const searchText = String(manageAccessState.searchText || '').trim().toLowerCase();
    const accessFilter = String(manageAccessState.accessFilter || 'ALL').trim().toUpperCase();
    const allowOverrideOnly = Boolean(manageAccessState.allowOverrideOnly);

    return manageAccessState.items.filter((item) => {
      const displayName = String(item?.displayName || '').trim().toLowerCase();
      const itemAccessLevel = String(item?.accessLevel || 'NONE').trim().toUpperCase();
      const itemAllowOverride = Boolean(item?.allowOverride);

      if (searchText && !displayName.includes(searchText)) {
        return false;
      }

      if (accessFilter !== 'ALL' && itemAccessLevel !== accessFilter) {
        return false;
      }

      if (allowOverrideOnly && !itemAllowOverride) {
        return false;
      }

      return true;
    });
  }, [
    manageAccessState.accessFilter,
    manageAccessState.allowOverrideOnly,
    manageAccessState.items,
    manageAccessState.searchText,
  ]);

  function handleEditClick(rota) {
    if (!rota?.masterRotaId) {
      toast.error('❌ Unable to open this rota for editing.', TOAST_CONFIG);
      return;
    }
    navigate(`/app/sysmanager/rotas/${rota.masterRotaId}/edit`);
  }

  function closeManageAccessModal() {
    setManageAccessState(createManageAccessState());
    setUpdatingCogIds({});
  }

  async function handleManageAccessClick(rota) {
    if (!rota?.masterRotaId) {
      toast.error('❌ Unable to open the access manager for this rota.', TOAST_CONFIG);
      return;
    }

    setManageAccessState({
      open: true,
      rota,
      loading: true,
      items: [],
      searchText: '',
      accessFilter: 'ALL',
      allowOverrideOnly: false,
    });
    setUpdatingCogIds({});

    try {
      const response = await getRotaStaffPerms(rota.masterRotaId);
      const loadedItems = Array.isArray(response?.items) ? response.items : [];
      setManageAccessState((currentValue) => ({
        ...currentValue,
        loading: false,
        items: loadedItems,
      }));
    } catch (error) {
      console.error('Failed to load rota staff permissions', error);
      toast.error(
        `❌ Failed to load staff permissions for ${rota.rotaName || 'this rota'}.`,
        TOAST_CONFIG,
      );
      setManageAccessState((currentValue) => ({
        ...currentValue,
        loading: false,
        items: [],
      }));
    }
  }

  function setStaffUpdateState(targetCogId, nextStatePatch) {
    setUpdatingCogIds((currentValue) => ({
      ...currentValue,
      [targetCogId]: {
        ...createUpdatingState(),
        ...(currentValue[targetCogId] || {}),
        ...nextStatePatch,
      },
    }));
  }

  function clearStaffUpdateState(targetCogId, stateKey) {
    setUpdatingCogIds((currentValue) => {
      const existingState = {
        ...createUpdatingState(),
        ...(currentValue[targetCogId] || {}),
      };
      existingState[stateKey] = false;

      if (!existingState.accessLevel && !existingState.allowOverride) {
        const nextValue = { ...currentValue };
        delete nextValue[targetCogId];
        return nextValue;
      }

      return {
        ...currentValue,
        [targetCogId]: existingState,
      };
    });
  }

  async function handleAccessLevelChange(staffMember, nextAccessLevel) {
    const targetCogId = String(staffMember?.cogId || '').trim();
    const displayName = String(staffMember?.displayName || 'This user').trim();
    const masterRotaId = String(manageAccessState?.rota?.masterRotaId || '').trim();
    if (!targetCogId || !masterRotaId) {
      toast.error(`❌ Failed to update rota access for ${displayName}.`, TOAST_CONFIG);
      return;
    }

    setStaffUpdateState(targetCogId, { accessLevel: true });

    try {
      const response = await updateRotaStaffPerms({
        masterRotaId,
        targetCogId,
        accessLevel: nextAccessLevel,
      });
      const savedAccessLevel = String(response?.accessLevel || nextAccessLevel).trim().toUpperCase();

      setManageAccessState((currentValue) => ({
        ...currentValue,
        items: currentValue.items.map((item) => (
          item.cogId === targetCogId
            ? { ...item, accessLevel: savedAccessLevel }
            : item
        )),
      }));

      toast.success(
        `✅ Updated rota access for ${displayName} to ${ACCESS_LABEL_LOOKUP[savedAccessLevel] || savedAccessLevel.toLowerCase()}.`,
        TOAST_CONFIG,
      );
    } catch (error) {
      console.error('Failed to update rota access', error);
      toast.error(`❌ Failed to update rota access for ${displayName}.`, TOAST_CONFIG);
    } finally {
      clearStaffUpdateState(targetCogId, 'accessLevel');
    }
  }

  async function handleAllowOverrideToggle(staffMember, nextAllowOverride) {
    const targetCogId = String(staffMember?.cogId || '').trim();
    const displayName = String(staffMember?.displayName || 'This user').trim();
    const masterRotaId = String(manageAccessState?.rota?.masterRotaId || '').trim();
    if (!targetCogId || !masterRotaId) {
      toast.error(`❌ Failed to update override access for ${displayName}.`, TOAST_CONFIG);
      return;
    }

    setStaffUpdateState(targetCogId, { allowOverride: true });

    try {
      const response = await updateRotaStaffAllowOverride({
        masterRotaId,
        targetCogId,
        allowOverride: Boolean(nextAllowOverride),
      });
      const savedAllowOverride = Boolean(response?.allowOverride);

      setManageAccessState((currentValue) => ({
        ...currentValue,
        items: currentValue.items.map((item) => (
          item.cogId === targetCogId
            ? { ...item, allowOverride: savedAllowOverride }
            : item
        )),
      }));

      toast.success(
        `✅ Updated override access for ${displayName} to ${savedAllowOverride ? 'on' : 'off'}.`,
        TOAST_CONFIG,
      );
    } catch (error) {
      console.error('Failed to update rota override access', error);
      toast.error(`❌ Failed to update override access for ${displayName}.`, TOAST_CONFIG);
    } finally {
      clearStaffUpdateState(targetCogId, 'allowOverride');
    }
  }

  useEffect(() => {
    const sysManagerPerms = userAppData?.sysManager || {};
    if (!sysManagerPerms || sysManagerPerms.perms !== 1 || !sysManagerPerms.rotaAdmin) {
      navigate('/app');
      return;
    }

    async function loadRotas() {
      setLoading(true);
      try {
        const response = await getRotasBootstrap();
        const loadedRotas = Array.isArray(response?.rotas) ? response.rotas : [];
        setRotas(loadedRotas);
      } catch (error) {
        console.error('Failed to load rota list', error);
        toast.error('❌ Failed to load rota data. Please refresh and try again.', TOAST_CONFIG);
      } finally {
        setLoading(false);
      }
    }

    loadRotas();
  }, [navigate]);

  return (
    <div className='main_div'>
      <div className='internal_main_div sysManager_rotas_container'>
        <Header
          title='Rotas'
          subtitle='Review the saved rotas for your school, then jump into the dedicated create flow when you need to build a new one.'
          rightContent={<>
            <button className='module-page-header__button' type='button' onClick={() => navigate('/app/sysmanager/rotas/create')}>
              Create New Rota
            </button>
            <button className='module-page-header__button' type='button' onClick={() => navigate('/app/sysmanager')}>
              <FaArrowLeft aria-hidden='true' />
              Back to System Manager
            </button>
          </>}
        />

        {loading ? (
          <section className='sysManager_rotas_loadingCard'>
            <CgSpinner className='sysManager_rotas_spinner' />
            <p>Loading rota setup data...</p>
          </section>
        ) : (
          <section className='sysManager_rotas_listCard sysManager_rotas_listCard--main'>
            <div className='sysManager_rotas_cardHeader sysManager_rotas_cardHeader--centered'>
              <div>
                <h2 className='sysManager_rotas_cardTitle'>Current Rotas</h2>
                <p className='sysManager_rotas_cardText'>Grouped rota families you can edit are shown below.</p>
              </div>
            </div>

            {rotas.length === 0 ? (
              <div className='sysManager_rotas_emptyState'>
                No rotas are currently available for you to edit.
              </div>
            ) : (
              <div className='sysManager_rotas_tableWrap'>
                <table className='sysManager_rotas_table'>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Created by</th>
                      <th>Status</th>
                      <th>Active date range</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rotas.map((rota) => (
                      <tr key={rota.masterRotaId}>
                        <td>
                          <div className='sysManager_rotas_nameCell'>
                            <span>{rota.rotaName}</span>
                            {rota.variantCount > 1 && (
                              <p className='sysManager_rotas_variantHint'>
                                {rota.variantCount} variants
                              </p>
                            )}
                          </div>
                        </td>
                        <td>{rota.createdBy || 'Unknown user'}</td>
                        <td>{rota.status}</td>
                        <td>{rota.activeDateRange}</td>
                        <td>
                          <div className='sysManager_rotas_tableActionStack'>
                            <button
                              className='sysManager_rotas_secondaryButton sysManager_rotas_tableActionButton'
                              type='button'
                              onClick={() => handleEditClick(rota)}
                            >
                              Edit
                            </button>
                            <button
                              className='sysManager_rotas_secondaryButton sysManager_rotas_tableActionButton sysManager_rotas_manageButton'
                              type='button'
                              onClick={() => handleManageAccessClick(rota)}
                            >
                              <FaPerson size={16} />
                              <span>Manage Access</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {manageAccessState.open && (
          <div
            className='sysManager_rotas_accessModalOverlay'
            role='presentation'
            onClick={closeManageAccessModal}
          >
            <section
              className='sysManager_rotas_accessModal'
              role='dialog'
              aria-modal='true'
              aria-labelledby='sysManager-rotas-access-title'
              onClick={(event) => event.stopPropagation()}
            >
              <div className='sysManager_rotas_accessHeader'>
                <div>
                  <h2
                    className='sysManager_rotas_modalTitle'
                    id='sysManager-rotas-access-title'
                  >
                    Staff Permissions
                  </h2>
                  <p className='sysManager_rotas_modalSubtitle'>
                    Manage access levels for {manageAccessState?.rota?.rotaName || 'this rota'}.
                  </p>
                </div>
                <button
                  className='sysManager_rotas_secondaryButton'
                  type='button'
                  onClick={closeManageAccessModal}
                >
                  Close
                </button>
              </div>

              <div className='sysManager_rotas_accessBody'>
                <aside className='sysManager_rotas_accessFilters'>
                  <div className='sysManager_rotas_accessFiltersCard'>
                    <h3>Filters</h3>
                    <label
                      className={`sysManager_rotas_toggleCard sysManager_rotas_toggleCard--filter${manageAccessState.allowOverrideOnly ? ' sysManager_rotas_toggleCard--active' : ''}`}
                    >
                      <input
                        type='checkbox'
                        checked={manageAccessState.allowOverrideOnly}
                        onChange={(event) => (
                          setManageAccessState((currentValue) => ({
                            ...currentValue,
                            allowOverrideOnly: event.target.checked,
                          }))
                        )}
                      />
                      <span
                        className={`sysManager_rotas_toggleSwitch sysManager_rotas_toggleSwitch--filter${manageAccessState.allowOverrideOnly ? ' sysManager_rotas_toggleSwitch--active' : ''}`}
                        aria-hidden='true'
                      >
                        <span className='sysManager_rotas_toggleKnob' />
                      </span>
                      <span className='sysManager_rotas_toggleContent sysManager_rotas_toggleContent--filter'>
                        <span className='sysManager_rotas_toggleRow'>
                          <span className='sysManager_rotas_toggleTitle'>Allow Override</span>
                          <span
                            className={`sysManager_rotas_toggleSwitch sysManager_rotas_toggleSwitch--filter${manageAccessState.allowOverrideOnly ? ' sysManager_rotas_toggleSwitch--active' : ''}`}
                            aria-hidden='true'
                          >
                            <span className='sysManager_rotas_toggleKnob' />
                          </span>
                        </span>
                        <small className='sysManager_rotas_toggleDescription'>
                          Only show staff who currently have override enabled.
                        </small>
                      </span>
                    </label>
                    <div className='sysManager_rotas_accessFilterList'>
                      {ACCESS_FILTER_OPTIONS.map((option) => (
                        <label className='sysManager_rotas_accessFilterOption' key={option.value}>
                          <div className='sysManager_rotas_accessFilterControl'>
                            <input
                              type='radio'
                              name='sysmanager-rotas-access-filter'
                              value={option.value}
                              checked={manageAccessState.accessFilter === option.value}
                              onChange={(event) => (
                                setManageAccessState((currentValue) => ({
                                  ...currentValue,
                                  accessFilter: event.target.value,
                                }))
                              )}
                            />
                            <span>{option.label}</span>
                          </div>
                          <small>{option.description}</small>
                        </label>
                      ))}
                    </div>
                  </div>
                </aside>

                <div className='sysManager_rotas_accessMain'>
                  <div className='sysManager_rotas_accessToolbar'>
                    <input
                      className='sysManager_rotas_accessSearch'
                      type='text'
                      value={manageAccessState.searchText}
                      placeholder='Search staff...'
                      onChange={(event) => (
                        setManageAccessState((currentValue) => ({
                          ...currentValue,
                          searchText: event.target.value,
                        }))
                      )}
                    />
                  </div>

                  {manageAccessState.loading ? (
                    <div className='sysManager_rotas_accessLoading'>
                      <CgSpinner className='sysManager_rotas_spinner' />
                      <p>Loading staff permissions...</p>
                    </div>
                  ) : filteredManageAccessItems.length === 0 ? (
                    <div className='sysManager_rotas_emptyState'>
                      No staff matched the current search and filter settings.
                    </div>
                  ) : (
                    <div className='sysManager_rotas_accessTableWrap'>
                      <table className='sysManager_rotas_accessTable'>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Access Level</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredManageAccessItems.map((staffMember) => {
                            const staffCogId = String(staffMember?.cogId || '').trim();
                            const profileImageUrl = normaliseImageUrl(staffMember?.profileImageUrl);
                            const updatingState = {
                              ...createUpdatingState(),
                              ...(updatingCogIds[staffCogId] || {}),
                            };
                            const isUpdating = updatingState.accessLevel || updatingState.allowOverride;
                            const currentAccessLevel = String(staffMember?.accessLevel || 'NONE').trim().toUpperCase();
                            const allowOverride = Boolean(staffMember?.allowOverride);

                            return (
                              <tr key={staffCogId}>
                                <td>
                                  <div className='sysManager_rotas_accessUserCell'>
                                    {profileImageUrl ? (
                                      <div className='sysManager_rotas_accessAvatarWrap'>
                                        <div className='sysManager_rotas_accessAvatarPlaceholder'>
                                          {getAvatarInitial(staffMember?.displayName)}
                                        </div>
                                        <img
                                          className='sysManager_rotas_accessAvatar'
                                          src={profileImageUrl}
                                          alt={staffMember?.displayName || 'Staff member'}
                                          onError={(event) => {
                                            event.currentTarget.style.opacity = '0';
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      <div className='sysManager_rotas_accessAvatarPlaceholder'>
                                        {getAvatarInitial(staffMember?.displayName)}
                                      </div>
                                    )}
                                    <div className='sysManager_rotas_accessUserText'>
                                      <strong>{staffMember?.displayName || 'Unknown user'}</strong>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className='sysManager_rotas_accessControls'>
                                    <label
                                      className={`sysManager_rotas_accessToggle${allowOverride ? ' sysManager_rotas_accessToggle--active' : ''}${updatingState.allowOverride ? ' sysManager_rotas_accessToggle--disabled' : ''}`}
                                    >
                                      <input
                                        type='checkbox'
                                        checked={allowOverride}
                                        disabled={isUpdating}
                                        onChange={(event) => (
                                          handleAllowOverrideToggle(staffMember, event.target.checked)
                                        )}
                                      />
                                      <span
                                        className={`sysManager_rotas_accessToggleTrack${allowOverride ? ' sysManager_rotas_accessToggleTrack--active' : ''}`}
                                        aria-hidden='true'
                                      >
                                        <span className='sysManager_rotas_accessToggleKnob' />
                                      </span>
                                      <span className='sysManager_rotas_accessToggleLabel'>
                                        Override
                                      </span>
                                    </label>

                                    <select
                                      className='sysManager_rotas_accessSelect'
                                      value={currentAccessLevel}
                                      disabled={isUpdating}
                                      onChange={(event) => (
                                        handleAccessLevelChange(staffMember, event.target.value)
                                      )}
                                    >
                                      {ACCESS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>

                                    {isUpdating && (
                                      <div className='sysManager_rotas_accessUpdating'>
                                        <CgSpinner className='sysManager_rotas_buttonSpinner' />
                                        <span>Saving...</span>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
